import styles from './customer-details-grid.module.scss';
import { useContext, useState } from 'react';
import { ValueFormatterParams } from 'ag-grid-community';
import CustomerDetailsGridRenderer from '../customer-details-grid-renderer/customer-details-grid-renderer';
import { useGetRequestNewHook, useMakeRequestHook, useTableConfigHook } from '../../requestUtils';
import { GetCurrentCompany } from '../../customer-context/customer-context';
import WebkuaTable from '../../webkua-table/webkua-table';
import { UseSignalRHook } from '../../signalr-context/signalr-context';
import { booleanHeader, Box, CreateHeaders, dateHeader, HeaderSizeEnum, Icon, OrderTypesEnum, PrimaryButton, SecondaryButton, SnackBarContext, Tab, Tabs, textHeader } from '@frontend-workspace/react-shared-lib';
import EditFocusAccountDialog from '../edit-focus-account-dialog/edit-focus-account-dialog';
import AddCustomerGroupDialog from '../add-customer-group-dialog/add-customer-group-dialog';
import DeleteCustomerGroupDialog from '../delete-customer-group-dialog/delete-customer-group-dialog';
import { ICompanyUserAccounts } from '../../_models/company-user-accounts';
import { ICustomerGroupCompanies } from '../../_models/customer-group-companies';
import { useTranslation } from 'react-i18next';
import CreateFocusAccountDialog from '../create-focus-account-dialog/create-focus-account-dialog';

/* eslint-disable-next-line */
export interface CustomerDetailsGridProps {
  companyUserAccounts: ICompanyUserAccounts[],
  customerGroupCompanies: ICustomerGroupCompanies[],
  getUserAccounts: () => void,
  companyNumber: string
}

export function CustomerDetailsGrid(props: CustomerDetailsGridProps) {
  const companyGroupTableConfig = useTableConfigHook("CustomerDetailsCompanyGroup");
  const currentCompany = GetCurrentCompany();

  const { t } = useTranslation();
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const makeRequest = useMakeRequestHook();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [addCustomerGroup, setAddCustomerGroup] = useState<boolean>(false);
  const [deleteCustomerGroup, setDeleteCustomerGroup] = useState<ICustomerGroupCompanies | undefined>(undefined);

  const openAddCustomerGroupDialog = () => setAddCustomerGroup(true);
  const closeAddCustomerGroupDialog = () => setAddCustomerGroup(false);

  const closeDeleteCustomerGroupDialog = () => setDeleteCustomerGroup(undefined);
  const openDeleteCustomerGroupDialog = (e: any) => setDeleteCustomerGroup(e.data);

  const navigateToTabOne = () => setSelectedTab(0);
  const navigateToTabTwo = () => setSelectedTab(1);

  const handleCreateCompanyGroup = () => {
    makeRequest(`companies/${currentCompany?.number}/action/createGroup`, "POST").then(result => {
      setAddCustomerGroup(true);
      setSnackbarMessage({
        content: t('standard.createsCustomer'),
        type: "info"
      });
    });
  };

  const deleteAccountRenderer = (params: ValueFormatterParams) => (
    <SecondaryButton
      variant="outlined"
      size='small'
      onClick={() => openDeleteCustomerGroupDialog(params)}>
      {t('standard.delete')}
    </SecondaryButton>
  );

  return <>
    <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '20px' }}>
      <Tabs
        value={selectedTab}
        variant="scrollable"
        scrollButtons="auto">
        <Tab label={t('customerDetails.focusAccounts')} onClick={navigateToTabOne} />
        <Tab label={t('customerDetails.companyGroup')} onClick={navigateToTabTwo} />
      </Tabs>
    </Box>
    <div>
      {selectedTab === 0 && <UserAccounts />}
      {selectedTab === 1 &&
        <>
          <div className="d-flex flex-row">
            <div className="mb-3 mb-sm-0">
              {props.customerGroupCompanies.length === 0 ?
                <PrimaryButton
                  variant="contained"
                  onClick={handleCreateCompanyGroup}>
                  <Icon className="me-1" fontSize="small">add</Icon>
                  {t('customerDetails.createCompanyGroup')}
                </PrimaryButton> :
                <PrimaryButton
                  variant="contained"
                  onClick={openAddCustomerGroupDialog}>
                  <Icon className="me-1" fontSize="small">add</Icon>
                  {t('standard.addCompanyDepartment')}
                </PrimaryButton>}
            </div>
          </div>
          <WebkuaTable
            headers={CreateHeaders([
              textHeader("companyNumber", t('standard.companyNumber'), HeaderSizeEnum.EXTRA_SMALL),
              textHeader("payerCode", t('standard.payerCode'), HeaderSizeEnum.EXTRA_SMALL),
              textHeader("name", t('standard.name'), HeaderSizeEnum.EXTRA_LARGE),
              textHeader("", "", HeaderSizeEnum.SMALL, {
                renderer: deleteAccountRenderer,
                cellStyle: { justifyContent: 'right' },
                headerClass: "ag-right-aligned-header"
              })
            ])}
            data={props.customerGroupCompanies}
            tableConfig={companyGroupTableConfig}
          />
        </>}
    </div>
    <AddCustomerGroupDialog
      open={!!addCustomerGroup}
      onClose={closeAddCustomerGroupDialog}
      companyNumber={props.companyNumber}
    />
    {deleteCustomerGroup &&
      <DeleteCustomerGroupDialog
        open={!!deleteCustomerGroup}
        onClose={closeDeleteCustomerGroupDialog}
        deleteCustomerGroup={deleteCustomerGroup}
        companyNumber={props.companyNumber}
      />}
  </>;

}
function UserAccounts(props: {
}) {
  const { t } = useTranslation();
  const currentCompany = GetCurrentCompany();
  const accountTableConfig = useTableConfigHook("CustomerDetailsAccounts");
  const companyNumber = currentCompany ? currentCompany.number : "";
  const { data: companyUserAccounts, getData: getUserAccounts, isLoading }: { data: ICompanyUserAccounts[], getData: () => void, isLoading: boolean } = useGetRequestNewHook(`companies/${companyNumber}/userAccounts`, [], [currentCompany]);

  const [showGroup, setShowGroup] = useState<boolean>(false);
  const [showDeleted, setShowDeleted] = useState<boolean>(false);
  const [editFocusAccount, setEditFocusAccount] = useState<ICompanyUserAccounts | undefined>(undefined);

  const openEditFocusAccountDialog = (e: any) => setEditFocusAccount(e.data);
  const closeEditFocusAccountDialog = () => setEditFocusAccount(undefined)


  const [newFocusAccount, setNewFocusAccount] = useState(false);
  const openNewFocusAccountDialog = () => setNewFocusAccount(true);
  const closeNewFocusAccountDialog = () => setNewFocusAccount(false);


  UseSignalRHook(getUserAccounts, [
    OrderTypesEnum.NewUser,
    OrderTypesEnum.ChangeLicenseAccess,
    OrderTypesEnum.LicenseAccessChangeOrder,
    OrderTypesEnum.DeleteAccount,
    OrderTypesEnum.ReopenUserAccount,
    OrderTypesEnum.AdminEditAccount,
    OrderTypesEnum.ResendInviteOrder,
    OrderTypesEnum.FinishUnregisteredUserOrder,
    OrderTypesEnum.MoveAccountsToCompanyOrder
  ]);

  const createAccountRenderer = (params: ValueFormatterParams) => (
    <PrimaryButton
      variant="outlined"
      size='small'
      onClick={() => openEditFocusAccountDialog(params)}>
      {t('standard.change')}
    </PrimaryButton>
  );

  const filteredAccounts = companyUserAccounts
    .filter(ua => showGroup ? true : ua.companyNumber === companyNumber)
    .filter(ua => !showDeleted ? !ua.isDeleted : true)

  return <>
    <PrimaryButton
      variant="contained"
      onClick={() => openNewFocusAccountDialog()}>
      <Icon className="me-1" fontSize="small">add</Icon>{t("standard.createFocusAccount")}
    </PrimaryButton>

    <WebkuaTable
      isLoading={isLoading}
      onRefresh={getUserAccounts}
      headers={CreateHeaders([
        textHeader("firstName", t('standard.firstName'), HeaderSizeEnum.LARGE, {
          renderer: 'agGroupCellRenderer',
          cellStyle: { fontSize: '0.875rem', fontWeight: '600' },
        }),
        textHeader("lastName", t('standard.lastName'), HeaderSizeEnum.LARGE),
        textHeader("focusAccount", t('standard.focusAccount'), HeaderSizeEnum.EXTRA_LARGE),
        textHeader("email", t('standard.email'), HeaderSizeEnum.EXTRA_LARGE),
        textHeader("role", t('standard.role'), HeaderSizeEnum.LARGE),
        textHeader("phoneNumber", t('standard.phone'), HeaderSizeEnum.LARGE),
        booleanHeader("hasSupport", t('standard.userSupport')),
        booleanHeader("isAcademic", t('standard.academic'), { hide: true }),
        textHeader("accessToLicenses", t('standard.accesses'), HeaderSizeEnum.SMALL, {
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        }),
        textHeader("coordinatorForLicenses", t('standard.coordinator'), HeaderSizeEnum.SMALL, {
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        }),
        textHeader("companyNumber", t('standard.companyNo'), HeaderSizeEnum.MEDIUM, { hide: !showGroup }),
        textHeader("companyName", t('standard.companyName'), HeaderSizeEnum.LARGE, { hide: !showGroup }),
        dateHeader("createdTime", t('standard.created'), { hide: true }),
        booleanHeader("isDeleted", t('standard.deleted'), { hide: !showDeleted }),
        booleanHeader("isActivated", t('customerDetails.loggedInToMyPage'), { hide: true }),
        textHeader("", "", HeaderSizeEnum.LARGE, {
          renderer: createAccountRenderer,
          cellStyle: { justifyContent: 'right' }
        })
      ])}
      expandComponent={CustomerDetailsGridRenderer}
      data={filteredAccounts}
      tableConfig={accountTableConfig}
      tableFilters={[{
        label: t('customerDetails.viewByGroup'),
        checked: showGroup,
        onChange: setShowGroup
      }, {
        label: t('customerDetails.showDeleted'),
        checked: showDeleted,
        onChange: setShowDeleted
      }]}
    />
    {editFocusAccount && <EditFocusAccountDialog
      open={!!editFocusAccount}
      onClose={closeEditFocusAccountDialog}
      getUserAccounts={getUserAccounts}
      id={editFocusAccount.id}
    />}

    {newFocusAccount && <CreateFocusAccountDialog
      open={!!newFocusAccount}
      onClose={closeNewFocusAccountDialog}
    />}

  </>;
}

export default CustomerDetailsGrid;